import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { cfpBaseUrl } from '../../network/urls';
import { resolveScopedTableOfficeCode, resolveTableApiOfficeLevel } from '../../utils/adminCompany';

const initialState = {
    data: [],
    currentPage: 1,
    pageSize: 20,
    dateFrom: '',
    dateTo: '',
    applicationProgressId: '',
    objectionStatus: '',
    totalPages: null,
    totalRecords: null,
    isLoading: false,
    error: null,
}

export const fetchPaginatedDisconnectionOverdue = createAsyncThunk(
    'generalServices/fetchDisconnectionOverdue',
    async ({ page, pageSize, dateFrom, dateTo, applicationProgressId, objectionStatus, officeCode }, { getState }) => {
        const { auth } = getState();
        const { token, code, hierarchy, cfcLevelId, showAllApplicationsFlag, roleId, userId, selectedAdminCompanyCode } = auth;
        if (!token) {
            return { status: "400", data: [], totalPages: 1, totalRecords: 0 };
        }

        const myHeaders = new Headers();
        myHeaders.append("Authorization", token);
        myHeaders.append("Content-Type", "application/json");

        const effectiveOfficeCode = resolveScopedTableOfficeCode(code, roleId, officeCode, selectedAdminCompanyCode);
        const effectiveOfficeLevel = resolveTableApiOfficeLevel({
            userCode: code,
            effectiveOfficeCode,
            hierarchy,
            cfcLevelId,
        });
        const raw = JSON.stringify({
            pageNumber: String(page || 1),
            pageSize: String(pageSize || 20),
            officeCode: String(effectiveOfficeCode ?? ""),
            officeLevel: String(effectiveOfficeLevel ?? ""),
            showAllApplicationFlag: String(showAllApplicationsFlag ?? ""),
            overDueFlag: "1",
            dateFrom: dateFrom ? dateFrom : "",
            dateTo: dateTo ? dateTo : "",
            applicationProgressId:
                applicationProgressId === 0 ||
                applicationProgressId === "0" ||
                (applicationProgressId !== undefined &&
                    applicationProgressId !== null &&
                    applicationProgressId !== "")
                    ? String(applicationProgressId)
                    : "",
            objectionStatus: objectionStatus ? String(objectionStatus) : "0",
            userId: roleId == 113 || roleId == 1146 ? String(userId ?? "") : "",
        });

        const requestOptions = {
            method: "POST",
            headers: myHeaders,
            body: raw,
            redirect: "follow"
        };

        const response = await fetch(cfpBaseUrl + `dco/getAllApplications`, requestOptions);
        let result;
        try {
            result = await response.json();
        } catch {
            throw new Error('Unable to fetch disconnection applications!');
        }

        const status = String(result?.status ?? response.status);
        if (status === "400") {
            return { status: "400", data: [], totalPages: 1, totalRecords: 0 };
        }
        if (status !== "200") {
            throw new Error(result?.message || 'Unable to fetch disconnection applications!');
        }

        return result;
    }
);

const disconnectionOverduePaginatedSlice = createSlice({
    name: 'disconnectionOverduePaginated',
    initialState: initialState,
    reducers: {
        nextPage(state) {
            state.currentPage += 1;
        },
        previousPage(state) {
            if (state.currentPage > 1) {
                state.currentPage -= 1;
            }
        },
        filterProgressId(state, action) {
            state.applicationProgressId = action.payload
        },
        resetProgressIdFilter(state) {
            state.applicationProgressId = ''
        },
        filterObjectionStatus(state, action) {
            state.objectionStatus = action.payload
        },
        resetObjectionStatusFilter(state) {
            state.applicationProgressId = ''
        },
        filterDateFrom(state, action) {
            state.dateFrom = action.payload
        },
        filterDateTo(state, action) {
            state.dateTo = action.payload
        },
        resetDateFilter(state) {
            state.dateFrom = ''
            state.dateTo = ''
        },
        firstPage(state) {
            state.currentPage = 1;
        },
        lastPage(state, action) {
            state.currentPage = action.payload;
        },
        resetState(state) {
            state.data = []
            state.currentPage = 1
            state.pageSize = 20
            state.dateFrom = ''
            state.dateTo = ''
            state.applicationProgressId = ''
            state.objectionStatus = ''
            state.totalPages = null
            state.totalRecords = null
            state.isLoading = false
            state.error = null
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchPaginatedDisconnectionOverdue.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchPaginatedDisconnectionOverdue.fulfilled, (state, action) => {
                state.isLoading = false;
                if (action.payload?.data) {
                    state.data = action.payload.data;
                } else {
                    state.data = [];
                }
                if (action.payload?.totalPages) {
                    state.totalPages = action.payload.totalPages;
                } else {
                    state.totalPages = 1;
                }
                state.totalRecords = action.payload?.totalRecords ?? 0;
            })
            .addCase(fetchPaginatedDisconnectionOverdue.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.error.message;
                state.totalPages = null;
                state.totalRecords = null;
            });
    },
});

export const { nextPage, previousPage, firstPage, lastPage, filterProgressId, filterObjectionStatus, filterDateFrom, filterDateTo, resetProgressIdFilter, resetObjectionStatusFilter, resetDateFilter, resetState } = disconnectionOverduePaginatedSlice.actions;

export default disconnectionOverduePaginatedSlice.reducer;
